// Statement PDFs, the part that needs no pdf.js: the text items of a PDF (what `getTextContent()` returns,
// reduced to page, position and text) in, a parsed statement out. The phone gets the items from pdf.js running
// in Hermes, the web and the MCP all through ./pdf.ts (readPdf).
import type { ParsedStatement } from "@paisa/types"
import { isAmexCreditCardPdf, parseAmexCreditCardItems } from "./amex/credit-card-pdf"
import { type GeneralMiss, readGeneralStatement } from "./general/statement"
import {
  isMilleniaCreditCardPdf,
  isSwiggyhdfcCreditCardPdf,
  isTataNeuCreditCardPdf,
  parseHdfcCreditCardItems,
} from "./hdfc/cc-pdf"
import { isHdfcCCYearlyPdf, parseHdfcCCYearlyItems } from "./hdfc/cc-yearly-pdf"
import { isHdfcSavingsPdf, parseHdfcSavingsStatementItems } from "./hdfc/savings-pdf"
import { isSbiSavingsPdf, parseSbiSavingsItems } from "./sbi/savings-pdf"

/** One piece of text on a PDF page; x and y are the PDF's own units (origin bottom-left), rounded. */
export interface PdfItem {
  page: number
  x: number
  y: number
  str: string
  /** Text width in PDF units. */
  w: number
}

const ROUTES: { match: (raw: string) => boolean; parse: (items: PdfItem[]) => ParsedStatement }[] =
  [
    // Yearly statements before monthly: both share bank keywords, yearly has "Year End Statement"
    { match: isHdfcCCYearlyPdf, parse: parseHdfcCCYearlyItems },
    { match: isSwiggyhdfcCreditCardPdf, parse: parseHdfcCreditCardItems },
    { match: isTataNeuCreditCardPdf, parse: parseHdfcCreditCardItems },
    { match: isMilleniaCreditCardPdf, parse: parseHdfcCreditCardItems },
    { match: isAmexCreditCardPdf, parse: parseAmexCreditCardItems },
    { match: isSbiSavingsPdf, parse: parseSbiSavingsItems },
    { match: isHdfcSavingsPdf, parse: parseHdfcSavingsStatementItems },
  ]

export type PdfItems = PdfItem[]

/** The part of a pdf.js document read here (structural: nothing of pdf.js is imported by this file). */
export interface PdfDoc {
  numPages: number
  getPage(n: number): Promise<{
    getTextContent(): Promise<{ items: readonly unknown[] }>
  }>
}

type TextItem = { str: string; transform: number[]; width?: number }

/** The text items of an opened pdf.js document. */
export async function pdfItemsOf(pdf: PdfDoc): Promise<PdfItems> {
  const result: PdfItem[] = []
  for (let p = 1; p <= pdf.numPages; p++) {
    const content = await (await pdf.getPage(p)).getTextContent()
    for (const item of content.items) {
      // TextItem has `str`; TextMarkedContent has `type` — guard for TextItem
      const ti = item as TextItem
      if (typeof ti.str !== "string" || !ti.str.trim()) continue
      result.push({
        page: p,
        x: Math.round(ti.transform[4]),
        y: Math.round(ti.transform[5]),
        str: ti.str,
        w: Math.round(ti.width ?? 0),
      })
    }
  }
  return result
}

/** A statement read from a PDF's items, or why it couldn't be ('password' comes only from opening the file). */
export type StatementRead =
  | { ok: true; statement: ParsedStatement }
  | { ok: false; reason: GeneralMiss }

/**
 * The bank's parser for these items (detected from the first two pages, like the web). When no bank parser
 * knows the file, the general reader (./general/statement.ts), which returns only rows its balance math proves.
 */
export function readPdfStatement(items: readonly PdfItem[]): StatementRead {
  const raw = items
    .filter((i) => i.page <= 2)
    .map((i) => i.str)
    .join(" ")
  const route = ROUTES.find((r) => r.match(raw))
  if (route) return { ok: true, statement: route.parse([...items]) }
  const general = readGeneralStatement(items)
  return general.ok ? { ok: true, statement: general.statement } : general
}

/** The bank's parser for these items, else the general reader; null when neither reads them. */
export function parsePdfText(items: readonly PdfItem[]): ParsedStatement | null {
  const read = readPdfStatement(items)
  return read.ok ? read.statement : null
}
